// The shell: a header strip and a session gate. Signed out, the page says
// what the lab is and offers sign-in; signed in, it is the lab. Whether the
// signed-in subject may transcribe is the Worker's call (the allow-list),
// so a refused subject finds out on submit, with the reason beside it.
import { signIn, signOut } from "@bindery/auth-consumer/browser";
import { useTokenSession } from "@bindery/auth-consumer/react";
import { SessionGate } from "@bindery/ui/components/session/session-gate";
import { Button } from "@bindery/ui/components/ui/button";
import { LogIn, LogOut, RotateCw } from "lucide-react";
import { Panel } from "./components/panel";
import { ComposePage } from "./compose/compose-page";
import { useHashView, type View } from "./lib/hooks";
import { Lab } from "./lab";

function Header({ signedIn, view }: { signedIn: boolean; view: View }) {
  return (
    <header className="bg-foreground text-background print:hidden">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2">
        <span className="font-mono text-sm font-medium tracking-tight">
          speech<span className="text-[var(--lab-signal)]">·</span>lab
        </span>
        <span className="lab-label text-background/60 hidden sm:inline">
          @bindery/speech bench harness · nothing is stored
        </span>
        <nav className="lab-label flex gap-3" aria-label="Pages">
          <a
            href="#bench"
            aria-current={view === "bench" ? "page" : undefined}
            className={
              view === "bench" ? "text-background" : "text-background/60"
            }
          >
            bench
          </a>
          <a
            href="#compose"
            aria-current={view === "compose" ? "page" : undefined}
            className={
              view === "compose" ? "text-background" : "text-background/60"
            }
          >
            compose
          </a>
        </nav>
        {signedIn ? (
          <Button
            size="xs"
            variant="ghost"
            className="hover:bg-background/10 hover:text-background ml-auto"
            onClick={() => signOut()}
          >
            <LogOut /> Sign out
          </Button>
        ) : null}
      </div>
    </header>
  );
}

function SignedOut() {
  return (
    <Panel index="00" title="Sign in" className="mx-auto max-w-xl">
      <p className="text-sm leading-relaxed">
        The lab records a spoken answer, sends it through{" "}
        <code className="lab-readout">@bindery/speech</code> and shows
        everything that comes back. It is for team members on the lab&rsquo;s
        allow-list; the transcription providers bill per call.
      </p>
      <Button className="self-start" onClick={() => signIn()}>
        <LogIn /> Sign in
      </Button>
    </Panel>
  );
}

function Unreachable() {
  return (
    <Panel index="00" title="Session" className="mx-auto max-w-xl">
      <p className="text-sm leading-relaxed">
        Your sign-in could not be checked because the sign-in service did not
        answer. Try again in a moment.
      </p>
      <Button className="self-start" onClick={() => window.location.reload()}>
        <RotateCw /> Try again
      </Button>
    </Panel>
  );
}

export function App({ authError }: { authError: string | null }) {
  const session = useTokenSession();
  const view = useHashView();
  return (
    <div className="text-foreground min-h-screen">
      <Header signedIn={session.kind === "ready"} view={view} />
      <main className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4">
        {authError === null ? null : (
          <p
            role="alert"
            className="border-destructive/40 bg-destructive/5 text-destructive rounded-sm border px-3 py-2 font-mono text-xs"
          >
            sign-in failed: {authError}
          </p>
        )}
        <SessionGate
          status={session.kind}
          onSignIn={() => signIn()}
          loading={<p className="lab-label">checking session…</p>}
          signedOut={<SignedOut />}
          unreachable={<Unreachable />}
        >
          {view === "compose" ? <ComposePage /> : <Lab />}
        </SessionGate>
      </main>
    </div>
  );
}
